import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Header } from "@/components/Header";
import { ClientePicker } from "@/components/ClientePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Wallet, Plus, Search, FileSpreadsheet, FileText, CheckCircle2, Trash2, Pencil, ArrowLeft, Undo2, Banknote, HandCoins, Landmark, Layers, Calculator, BarChart3, Send, Bell, XCircle, Upload, ChevronsUpDown, ChevronUp, ChevronDown, AlertTriangle, ShieldCheck, History } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ManualeButton } from "@/components/ManualeButton";
import { BonificiTab, EstrattiTab, SubagenziaTab, QuietanzamentoTab, AssimocoTab } from "@/pages/contabilita/OperativitaTabs";
import { RiconciliazioneTab } from "@/pages/contabilita/RiconciliazioneTab";
import { useAuth } from "@/context/AuthContext";
import { DateInputIT } from "@/components/DateInputIT";
import { fmtIT } from "@/lib/dateIT";

const CHART_COLORS = ["#0d9488", "#3b82f6", "#f59e0b", "#8b5cf6", "#ef4444", "#10b981", "#6366f1", "#ec4899", "#64748b"];

const euro = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (v) => {
  if (!v) return "—";
  const s = String(v).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
};
const isExpiredTitolo = (t) => {
  if (!t?.data_scadenza) return false;
  if (!["DA_INCASSARE", "ARRETRATO"].includes(t.stato_effettivo)) return false;
  return String(t.data_scadenza).slice(0, 10) < new Date().toISOString().slice(0, 10);
};
const sortRows = (rows, sort, numKeys = []) => {
  if (!sort.key) return rows;
  const arr = [...rows].sort((a, b) => {
    const va = a?.[sort.key], vb = b?.[sort.key];
    if (numKeys.includes(sort.key)) return (Number(va) || 0) - (Number(vb) || 0);
    return String(va ?? "").toLowerCase().localeCompare(String(vb ?? "").toLowerCase(), "it", { numeric: true });
  });
  return sort.dir === "desc" ? arr.reverse() : arr;
};
const FRAZ = ["annuale", "semestrale", "quadrimestrale", "trimestrale", "mensile", "unica"];
const STATO_LABEL = { DA_INCASSARE: "Da incassare", INCASSATO: "Incassato", ARRETRATO: "Arretrato", ANTICIPATO: "Anticipato", STORNATO: "Stornato", PAGATO_A_COMPAGNIA: "Pagato a Compagnia" };
const BADGE = { DA_INCASSARE: "bg-amber-100 text-amber-700", INCASSATO: "bg-emerald-100 text-emerald-700", ARRETRATO: "bg-red-100 text-red-700", ANTICIPATO: "bg-violet-100 text-violet-700", STORNATO: "bg-slate-200 text-slate-500", PAGATO_A_COMPAGNIA: "bg-sky-100 text-sky-700" };
const emptyTit = { cliente_id: null, cliente_nome: "", numero_polizza: "", compagnia: "", ramo: "", premio_lordo: "", premio_netto: "", frazionamento: "annuale", data_effetto: "", data_scadenza: "", intermediario: "", note: "" };

export default function Contabilita() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDir = user?.role === "admin";
  const [section, setSection] = useState("titoli");
  const [active, setActive] = useState("portafoglio");
  const [items, setItems] = useState([]);
  const [sort, setSort] = useState({ key: "", dir: "asc" });
  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const [titoliHasMore, setTitoliHasMore] = useState(false);
  const [titoliTotal, setTitoliTotal] = useState(0);
  const [titoliPage, setTitoliPage] = useState(1);
  const [creditiQ, setCreditiQ] = useState("");
  const [crSort, setCrSort] = useState({ key: "", dir: "asc" });
  const crToggle = (k) => setCrSort((s) => s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" });
  const [creditiHasMore, setCreditiHasMore] = useState(false);
  const [creditiTotal, setCreditiTotal] = useState(0);
  const [creditiPage, setCreditiPage] = useState(1);
  const [stats, setStats] = useState(null);
  const [compagnie, setCompagnie] = useState([]);
  const [compagnieFiltro, setCompagnieFiltro] = useState([]);
  const [intermediari, setIntermediari] = useState([]);
  const [modalita, setModalita] = useState([]);
  const [produttoriFc, setProduttoriFc] = useState([]);
  const [cashModalita, setCashModalita] = useState([]);
  const [incDup, setIncDup] = useState(null);
  const [cassaUnif, setCassaUnif] = useState(null);
  const [canImport, setCanImport] = useState(false);
  const [impMgrOpen, setImpMgrOpen] = useState(false);
  const [impMgrs, setImpMgrs] = useState([]);
  const [impMgrNew, setImpMgrNew] = useState("");
  const [conti, setConti] = useState([]);
  const [crediti, setCrediti] = useState([]);
  const [fStato, setFStato] = useState("all");
  const [fComp, setFComp] = useState("all");
  const [fInt, setFInt] = useState("all");
  const [fDal, setFDal] = useState("");
  const [fAl, setFAl] = useState("");
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(null); // {mode, data}
  const [inc, setInc] = useState(null);
  const [contoView, setContoView] = useState(null); // {conto, movimenti}
  const [versa, setVersa] = useState(null);   // {movs, selected:[]}
  const [sospeso, setSospeso] = useState(null); // {codice, movs, selected:[], riferimento, importo_bonifico}
  const [recupera, setRecupera] = useState(null); // credito + {modalita, riferimento}
  const [chiusura, setChiusura] = useState(null); // [{codice,nome,atteso,contato,nota_rettifica}]
  const [chiusure, setChiusure] = useState([]);
  const [report, setReport] = useState(null); // {arretrati, perCompagnia, perCanale}
  const [sollecito, setSollecito] = useState(null); // {titolo, canale, destinatario, respinto}
  const [importing, setImporting] = useState(false);
  const [impOpen, setImpOpen] = useState(false);
  const [impBatches, setImpBatches] = useState([]);
  const [impResult, setImpResult] = useState(null);
  const [cleanup, setCleanup] = useState(null);
  const [confirmTxt, setConfirmTxt] = useState("");
  // Operazioni massive (incasso/cancellazione con flag + doppia conferma + audit)
  const [canMassive, setCanMassive] = useState(false);
  const [massMgrs, setMassMgrs] = useState([]);
  const [massMgrNew, setMassMgrNew] = useState("");
  const [massMgrOpen, setMassMgrOpen] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [selIds, setSelIds] = useState([]);   // titoli selezionati via flag
  const [bulk, setBulk] = useState(null);      // {mode, modalita, conferma, candidati, importo, protetti, data_incasso}
  const modLabel = (v) => (modalita.find((m) => m.value === v) || {}).label || v;

  const loadImpBatches = () => api.get("/contabilita/import-titoli/batches").then(({ data }) => setImpBatches(data)).catch(() => {});
  const loadCleanup = () => api.get("/contabilita/titoli/cleanup-preview").then(({ data }) => setCleanup(data)).catch(() => {});
  const openImp = () => { loadImpBatches(); loadCleanup(); setConfirmTxt(""); setImpOpen(true); };
  const doCleanup = async (scope, count) => {
    if (confirmTxt.trim().toUpperCase() !== "ELIMINA") { toast.error('Digita ELIMINA per confermare'); return; }
    if (!window.confirm(`Confermi l'eliminazione di ${count} titoli non incassati? L'operazione è definitiva.`)) return;
    try {
      const { data } = await api.post("/contabilita/titoli/cleanup", { scope, conferma: confirmTxt.trim() });
      toast.success(`${data.eliminati} titoli eliminati`, { duration: 7000 });
      setConfirmTxt(""); loadCleanup(); loadImpBatches(); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore pulizia"); }
  };
  const delImpBatch = async (b) => {
    if (!window.confirm(`Annullare l'import "${b.filename}"? Verranno eliminati i ${b.importati} titoli caricati non ancora incassati.`)) return;
    try {
      const { data } = await api.delete(`/contabilita/import-titoli/batches/${b.id}`);
      toast.success(`Import annullato · ${data.eliminati} titoli eliminati${data.conservati_incassati ? ` · ${data.conservati_incassati} già incassati conservati` : ""}`, { duration: 7000 });
      loadImpBatches(); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore annullamento"); }
  };
  const undoImpResult = async () => {
    if (!impResult?.batch_id) return;
    if (!window.confirm(`Annullare questo import? Verranno eliminati i ${impResult.importati} titoli appena caricati.`)) return;
    try {
      const { data } = await api.delete(`/contabilita/import-titoli/batches/${impResult.batch_id}`);
      toast.success(`Import annullato · ${data.eliminati} titoli eliminati`, { duration: 6000 });
      setImpResult(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore annullamento"); }
  };

  const importTitoli = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      setImporting(true);
      const { data } = await api.post("/contabilita/import-titoli", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImpResult(data);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'import");
    } finally {
      setImporting(false);
    }
  };

  const saveImpMgrs = async (list) => {
    try { await api.post("/contabilita/import-managers", { emails: list }); setImpMgrs(list); toast.success("Autorizzati aggiornati"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const addImpMgr = () => {
    const e = impMgrNew.trim().toLowerCase();
    if (!e) return;
    if (impMgrs.includes(e)) { toast.error("Email già presente"); return; }
    saveImpMgrs([...impMgrs, e]); setImpMgrNew("");
  };
  const removeImpMgr = (e) => saveImpMgrs(impMgrs.filter((x) => x !== e));

  // --- Autorizzati operazioni massive + audit ---
  const saveMassMgrs = async (list) => {
    try { await api.post("/contabilita/massive-managers", { emails: list }); setMassMgrs(list); toast.success("Autorizzati massivi aggiornati"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const addMassMgr = () => {
    const e = massMgrNew.trim().toLowerCase();
    if (!e) return;
    if (massMgrs.includes(e)) { toast.error("Email già presente"); return; }
    saveMassMgrs([...massMgrs, e]); setMassMgrNew("");
  };
  const removeMassMgr = (e) => saveMassMgrs(massMgrs.filter((x) => x !== e));
  const loadAudit = () => api.get("/contabilita/massive-audit").then(({ data }) => setAuditRows(data)).catch(() => {});
  const openMassMgr = () => { loadAudit(); setMassMgrOpen(true); };
  const annullaMassiva = async (auditId) => {
    if (!window.confirm("Annullare questo incasso massivo? I titoli torneranno 'Da incassare' e i relativi movimenti verranno stornati.")) return;
    try {
      const { data } = await api.post(`/contabilita/massive-audit/${auditId}/annulla`);
      toast.success(`Operazione annullata: ${data.ripristinati} titoli riportati a 'Da incassare'`, { duration: 6000 });
      loadAudit(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // --- Selezione flag titoli ---
  const toggleSel = (id) => setSelIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allPageSelected = items.length > 0 && items.every((t) => selIds.includes(t.titolo_id));
  const toggleSelAll = () => setSelIds(allPageSelected
    ? selIds.filter((id) => !items.some((t) => t.titolo_id === id))
    : [...new Set([...selIds, ...items.map((t) => t.titolo_id)])]);
  const clearSel = () => setSelIds([]);

  // --- Operazioni massive con flag + doppia conferma ---
  const openBulk = async (mode) => {
    if (!selIds.length) { toast.error("Seleziona almeno un titolo con la casella."); return; }
    const endpoint = mode === "incasso" ? "/contabilita/titoli/incassa-massivo" : "/contabilita/titoli/cancella-massivo";
    try {
      const payload = { titolo_ids: selIds, dry_run: true };
      if (mode === "incasso") payload.modalita = "incasso_direzionale";
      const { data } = await api.post(endpoint, payload);
      if (!data.candidati) {
        toast.info(mode === "incasso"
          ? "Nessun titolo incassabile tra i selezionati (solo Da incassare/Arretrato)."
          : "Nessun titolo eliminabile tra i selezionati (i titoli già incassati sono protetti).");
        return;
      }
      setBulk({ mode, modalita: "incasso_direzionale", conferma: "", candidati: data.candidati,
                importo: data.importo_totale, protetti: data.protetti || 0,
                data_incasso: new Date().toISOString().slice(0, 10) });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const doBulk = async () => {
    const kw = bulk.mode === "incasso" ? "INCASSA" : "ELIMINA";
    if ((bulk.conferma || "").trim().toUpperCase() !== kw) { toast.error(`Digita ${kw} per confermare`); return; }
    try {
      if (bulk.mode === "incasso") {
        const { data } = await api.post("/contabilita/titoli/incassa-massivo", {
          titolo_ids: selIds, modalita: bulk.modalita, data_incasso: bulk.data_incasso, conferma: bulk.conferma.trim() });
        toast.success(`${data.applicati} titoli incassati (${euro(data.importo_totale)}) · ${modLabel(bulk.modalita)}`, { duration: 6000 });
      } else {
        const { data } = await api.post("/contabilita/titoli/cancella-massivo", {
          titolo_ids: selIds, conferma: bulk.conferma.trim() });
        toast.success(`${data.eliminati} titoli eliminati${data.protetti ? ` · ${data.protetti} già incassati protetti` : ""}`, { duration: 6000 });
      }
      setBulk(null); clearSel(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };


  const load = () => {
    loadTitoli(1, false);
    loadCrediti(1, false);
    api.get("/contabilita/dashboard").then(({ data }) => { setStats(data); setConti(data.conti || []); });
  };
  const loadCrediti = (pg = 1, append = false) => {
    const p = new URLSearchParams();
    if (creditiQ) p.set("q", creditiQ);
    p.set("page", pg);
    p.set("page_size", 50);
    api.get(`/contabilita/crediti?${p}`).then(({ data }) => {
      setCrediti((prev) => (append ? [...prev, ...data.items] : data.items));
      setCreditiHasMore(data.has_more);
      setCreditiTotal(data.total);
      setCreditiPage(data.page);
    });
  };
  const loadTitoli = (pg = 1, append = false) => {
    const p = new URLSearchParams();
    if (fStato !== "all") p.set("stato", fStato);
    if (fComp !== "all") p.set("compagnia", fComp);
    if (fInt !== "all") p.set("intermediario", fInt);
    if (fDal) p.set("dal", fDal);
    if (fAl) p.set("al", fAl);
    if (q) p.set("q", q);
    if (sort.key) { p.set("sort_by", sort.key); p.set("sort_dir", sort.dir); }
    p.set("page", pg);
    p.set("page_size", 50);
    api.get(`/contabilita/titoli?${p}`).then(({ data }) => {
      setItems((prev) => (append ? [...prev, ...data.items] : data.items));
      setTitoliHasMore(data.has_more);
      setTitoliTotal(data.total);
      setTitoliPage(data.page);
    });
  };
  useEffect(() => { load(); }, [fStato, fComp, fInt]); // eslint-disable-line
  const pagaCompagniaMassivo = async () => {
    const filtro = {};
    if (fComp !== "all") filtro.compagnia = fComp;
    if (fInt !== "all") filtro.intermediario = fInt;
    if (fStato !== "all") filtro.stato = fStato;
    if (!filtro.compagnia && !filtro.intermediario && !filtro.stato) {
      toast.error("Seleziona almeno un filtro (compagnia, intermediario o stato) prima dell'azione massiva.");
      return;
    }
    try {
      const { data: dry } = await api.post("/contabilita/titoli/incassa-massivo", { ...filtro, dry_run: true });
      if (!dry.candidati) { toast.info("Nessun titolo da incassare corrisponde ai filtri selezionati."); return; }
      if (!window.confirm(`Segnare ${dry.candidati} titoli (€ ${dry.importo_totale.toLocaleString("it-IT", { minimumFractionDigits: 2 })}) come "Pagato direttamente alla Compagnia"?\nNon verrà creato alcun movimento nella cassa fisica.`)) return;
      const { data } = await api.post("/contabilita/titoli/incassa-massivo", { ...filtro, modalita: "pagato_compagnia", conferma: "INCASSA" });
      toast.success(`${data.applicati} titoli segnati come "Pagato a Compagnia".`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore azione massiva"); }
  };
  const sortFirst = useRef(true);
  useEffect(() => {
    if (sortFirst.current) { sortFirst.current = false; return; }
    loadTitoli(1, false);
  }, [sort]); // eslint-disable-line
  const qFirst = useRef(true);
  useEffect(() => {
    if (qFirst.current) { qFirst.current = false; return; }
    const t = setTimeout(() => loadTitoli(1, false), 400);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line
  const creditiQFirst = useRef(true);
  useEffect(() => {
    if (creditiQFirst.current) { creditiQFirst.current = false; return; }
    const t = setTimeout(() => loadCrediti(1, false), 400);
    return () => clearTimeout(t);
  }, [creditiQ]); // eslint-disable-line
  useEffect(() => {
    api.get("/agency/compagnie").then(({ data }) => setCompagnie(data));
    api.get("/contabilita/compagnie").then(({ data }) => setCompagnieFiltro(data)).catch(() => {});
    api.get("/contabilita/intermediari").then(({ data }) => setIntermediari(data));
    api.get("/contabilita/modalita").then(({ data }) => setModalita(data));
    api.get("/contabilita/produttori-fc").then(({ data }) => { setProduttoriFc(data.produttori || []); setCashModalita(data.cash_modalita || []); }).catch(() => {});
    api.get("/contabilita/cassa-unificata").then(({ data }) => setCassaUnif(data)).catch(() => {});
    api.get("/contabilita/import-managers").then(({ data }) => { setCanImport(data.can_import); setImpMgrs(data.emails || []); }).catch(() => {});
    api.get("/contabilita/massive-managers").then(({ data }) => { setCanMassive(data.can_massive); setMassMgrs(data.emails || []); }).catch(() => {});
  }, []);

  const setD = (k, v) => setDialog((s) => ({ ...s, data: { ...s.data, [k]: v } }));
  const save = async () => {
    const d = dialog.data;
    const payload = { ...d, premio_lordo: Number(d.premio_lordo) || 0, premio_netto: Number(d.premio_netto) || 0 };
    try {
      if (dialog.mode === "edit") await api.put(`/contabilita/titoli/${d.titolo_id}`, payload);
      else await api.post("/contabilita/titoli", payload);
      toast.success("Titolo salvato"); setDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const doIncassa = async (force = false) => {
    try {
      await api.post(`/contabilita/titoli/${inc.titolo_id}/incassa`, {
        modalita: inc.modalita, data_incasso: inc.data_incasso, riferimento: inc.riferimento, collaboratore: inc.collaboratore, sede: inc.sede, tipo_incasso: inc.tipo_incasso, produttore_fc: inc.produttore_fc, force,
      });
      toast.success("Titolo incassato"); setInc(null); load();
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`${e.response.data.detail}\n\nVuoi registrarlo comunque?`)) return doIncassa(true);
        return;
      }
      toast.error(e.response?.data?.detail || "Errore");
    }
  };
  const doStorna = async (t) => { if (!window.confirm(`Stornare il titolo di ${t.cliente_nome}?`)) return; try { await api.post(`/contabilita/titoli/${t.titolo_id}/storna`); toast.success("Titolo stornato"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const del = async (t) => { if (!window.confirm("Eliminare il titolo?")) return; try { await api.delete(`/contabilita/titoli/${t.titolo_id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const openConto = async (c) => { const { data } = await api.get(`/contabilita/conti/${c.codice}/movimenti`); setContoView({ conto: c, movimenti: data }); };

  // Versa assegni
  const openVersa = async () => { const { data } = await api.get("/contabilita/assegni-da-versare"); setVersa({ movs: data, selected: data.map((m) => m.mov_id) }); };
  const toggleVersa = (id) => setVersa((v) => ({ ...v, selected: v.selected.includes(id) ? v.selected.filter((x) => x !== id) : [...v.selected, id] }));
  const doVersa = async () => { try { const { data } = await api.post("/contabilita/versa-assegni", { mov_ids: versa.selected }); toast.success(`${data.versati} assegni versati (${euro(data.importo)})`); setVersa(null); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };

  // Chiudi sospeso
  const openSospeso = async (codice) => { const { data } = await api.get(`/contabilita/sospesi/${codice}`); setSospeso({ codice, movs: data, selected: data.map((m) => m.mov_id), riferimento: "", importo_bonifico: "" }); };
  const toggleSospeso = (id) => setSospeso((s) => ({ ...s, selected: s.selected.includes(id) ? s.selected.filter((x) => x !== id) : [...s.selected, id] }));
  const doSospeso = async () => { try { const { data } = await api.post("/contabilita/chiudi-sospeso", { codice: sospeso.codice, mov_ids: sospeso.selected, riferimento: sospeso.riferimento, importo_bonifico: sospeso.importo_bonifico === "" ? null : Number(sospeso.importo_bonifico) }); toast.success(`Chiusi ${data.chiusi} titoli, bonifico ${euro(data.bonifico)}${data.differenza ? ` (diff ${euro(data.differenza)})` : ""}`); setSospeso(null); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };

  // Recupera credito
  useEffect(() => {
    if (!inc?.titolo_id) { setIncDup(null); return; }
    api.get(`/contabilita/titoli/${inc.titolo_id}/check-fc`).then(({ data }) => setIncDup(data.duplicato ? data.dettagli : null)).catch(() => setIncDup(null));
  }, [inc?.titolo_id]);

  const autoProduttore = (interm) => {
    const up = (interm || "").toUpperCase();    const tokens = up.replace(/'/g, " ").split(/\s+/);
    const found = produttoriFc.find((p) => p !== "Agenzia" && (tokens.includes(p.toUpperCase()) || up.includes(p.toUpperCase())));
    return found || "Agenzia";
  };
  const doRecupera = async () => { try { await api.post(`/contabilita/crediti/${recupera.credito_id}/recupera`, { modalita: recupera.modalita, riferimento: recupera.riferimento }); toast.success("Credito recuperato"); setRecupera(null); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };

  // Chiusura cassa
  const openChiusura = async () => { const { data } = await api.get("/contabilita/chiusura/preview"); setChiusura(data.map((r) => ({ ...r, contato: r.atteso, nota_rettifica: "" }))); api.get("/contabilita/chiusure").then(({ data }) => setChiusure(data)); };
  const setChiusuraRow = (i, k, v) => setChiusura((rows) => rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const doChiusura = async () => {
    try {
      await api.post("/contabilita/chiusura", { righe: chiusura.map((r) => ({ codice: r.codice, contato: Number(r.contato) || 0, nota_rettifica: r.nota_rettifica })) });
      toast.success("Chiusura cassa registrata"); setChiusura(null); load(); openChiusura();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // Report
  const openReport = async () => {
    const [a, c, k] = await Promise.all([
      api.get("/contabilita/report/arretrati"),
      api.get("/contabilita/report/per-compagnia"),
      api.get("/contabilita/report/per-canale"),
    ]);
    setReport({ arretrati: a.data, perCompagnia: c.data, perCanale: k.data });
  };
  const doSollecito = async () => {
    try {
      const url = sollecito.respinto ? `/contabilita/titoli/${sollecito.titolo.titolo_id}/finanziamento-respinto` : `/contabilita/titoli/${sollecito.titolo.titolo_id}/sollecito`;
      await api.post(url, { canale: sollecito.canale, destinatario: sollecito.destinatario, tipo: sollecito.respinto ? "finanziamento_respinto" : "standard" });
      toast.success(sollecito.respinto ? "Finanziamento respinto: titolo riaperto e sollecito inviato (SIMULATO)" : "Sollecito inviato (SIMULATO)");
      setSollecito(null); load(); if (report) openReport();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const exportUrl = (fmt) => {
    const p = new URLSearchParams();
    if (fStato !== "all") p.set("stato", fStato);
    if (fComp !== "all") p.set("compagnia", fComp);
    if (fInt !== "all") p.set("intermediario", fInt);
    if (fDal) p.set("dal", fDal);
    if (fAl) p.set("al", fAl);
    if (q) p.set("q", q);
    window.open(`${API}/contabilita/export/${fmt}?${p}`, "_blank");
  };

  const curMod = modalita.find((m) => m.value === inc?.modalita);

  const SECTIONS = [
    { key: "titoli", label: "Titoli & Incassi", icon: FileText, tabs: [
      { v: "portafoglio", l: "Portafoglio titoli" },
      { v: "crediti", l: "Crediti da recuperare" },
      { v: "quietanzamento", l: "Quietanze/Decadi" },
      { v: "assimoco", l: "Assimoco" },
    ] },
    { key: "cassa", label: "Cassa", icon: Wallet, tabs: [
      { v: "casse", l: "Casse & Conti" },
      { v: "chiusura", l: "Chiusura cassa", onClick: openChiusura },
    ] },
    { key: "banca", label: "Banca & Riconciliazione", icon: Landmark, tabs: [
      { v: "bonifici", l: "Bonifici" },
      { v: "estratti", l: "Estratti conto" },
      { v: "riconciliazione", l: "Riconciliazione" },
    ] },
    { key: "altro", label: "Subagenzia & Report", icon: Layers, tabs: [
      { v: "subagenzia", l: "Subagenzia" },
      { v: "report", l: "Report", onClick: openReport },
    ] },
  ];
  const curSection = SECTIONS.find((s) => s.key === section) || SECTIONS[0];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6"><ArrowLeft className="h-4 w-4" /> Torna alla home</button>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="flex items-center gap-2"><Wallet className="h-6 w-6 text-teal-600" /><h1 className="font-heading text-2xl font-medium text-slate-900">Contabilità di Agenzia</h1></div>
            <p className="text-slate-500 text-sm mt-1">Portafoglio titoli, casse e incassi per modalità</p>
          </div>
          <div className="flex gap-2">
            <ManualeButton file="Manuale_Operatore_Contabilita" route="/manuale/contabilita" />
            <Button variant="outline" onClick={() => exportUrl("excel")} data-testid="cont-export-excel"><FileSpreadsheet className="h-4 w-4 mr-2" /> Excel</Button>
            <Button variant="outline" onClick={() => exportUrl("pdf")} data-testid="cont-export-pdf"><FileText className="h-4 w-4 mr-2" /> PDF</Button>
            <Button onClick={() => setDialog({ mode: "new", data: { ...emptyTit } })} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="cont-add-btn"><Plus className="h-4 w-4 mr-2" /> Nuovo titolo</Button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Stat label="Da incassare (oggi)" value={stats.da_incassare_oggi} sub={`Settimana: ${stats.da_incassare_settimana} · Mese: ${stats.da_incassare_mese}`} />
            <Stat label="Arretrati" value={stats.arretrati} cls="text-red-600" sub={euro(stats.importo_arretrato)} />
            <Stat label="Incassato questo mese" value={euro(stats.incassato_mese)} cls="text-teal-600" />
            <Stat label="Crediti da recuperare" value={stats.crediti_aperti} cls="text-violet-600" sub={euro(stats.crediti_importo)} />
          </div>
        )}

        {/* Navigazione a 2 livelli: sezioni principali + sotto-tab */}
        <div className="flex flex-wrap gap-2 mb-4">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const activeSec = section === s.key;
            return (
              <button key={s.key} data-testid={`cont-sec-${s.key}`}
                onClick={() => { setSection(s.key); const first = s.tabs[0]; setActive(first.v); first.onClick?.(); }}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors border ${activeSec ? "bg-teal-600 text-white border-teal-600 shadow-sm" : "bg-white text-slate-600 border-slate-200 hover:border-teal-300 hover:text-teal-700"}`}>
                <Icon className="h-4 w-4" /> {s.label}
              </button>
            );
          })}
        </div>

        <Tabs value={active} onValueChange={setActive}>
          <TabsList className="mb-4 flex-wrap h-auto">
            {curSection.tabs.map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`cont-tab-${t.v}`} onClick={t.onClick}>{t.l}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="portafoglio">
            <div className="flex gap-3 mb-4 flex-wrap items-end">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cerca cliente o polizza…" className="pl-9 bg-white" data-testid="cont-search" />
              </div>
              <Select value={fStato} onValueChange={setFStato}><SelectTrigger className="w-40" data-testid="cont-filter-stato"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem>{Object.keys(STATO_LABEL).map((s) => <SelectItem key={s} value={s}>{STATO_LABEL[s]}</SelectItem>)}</SelectContent></Select>
              <Select value={fComp} onValueChange={setFComp}><SelectTrigger className="w-56" data-testid="cont-filter-comp"><SelectValue placeholder="Scegli la compagnia" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tutte le compagnie</SelectItem>{compagnieFiltro.map((nome) => <SelectItem key={nome} value={nome}>{nome}</SelectItem>)}</SelectContent></Select>
              <Select value={fInt} onValueChange={setFInt}><SelectTrigger className="w-56" data-testid="cont-filter-int"><SelectValue placeholder="Scegli l'intermediario" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tutti gli intermediari</SelectItem>{intermediari.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select>
              <div className="flex flex-col">
                <label className="text-[11px] text-slate-400 mb-0.5">Scadenza dal</label>
                <DateInputIT value={fDal} onChange={(v) => setFDal(v)} className="w-40 bg-white" testid="cont-filter-dal" />
              </div>
              <div className="flex flex-col">
                <label className="text-[11px] text-slate-400 mb-0.5">al</label>
                <DateInputIT value={fAl} onChange={(v) => setFAl(v)} className="w-40 bg-white" testid="cont-filter-al" />
              </div>
              <Button variant="outline" onClick={load}>Filtra</Button>
              {(fDal || fAl || fComp !== "all" || fInt !== "all" || fStato !== "all" || q) && (
                <Button variant="ghost" onClick={() => { setFStato("all"); setFComp("all"); setFInt("all"); setFDal(""); setFAl(""); setQ(""); setTimeout(load, 0); }} data-testid="cont-filter-reset">Azzera</Button>
              )}
              {canImport && (
                <>
                  <input type="file" accept=".csv" id="import-titoli-input" className="hidden" onChange={importTitoli} data-testid="cont-import-titoli-input" />
                  <Button variant="outline" disabled={importing} onClick={() => document.getElementById("import-titoli-input").click()} data-testid="cont-import-titoli-btn" title="Importa i titoli in arretrato dal CSV Assieasy">
                    <Upload className="h-4 w-4 mr-2" /> {importing ? "Importo…" : "Importa da Assieasy"}
                  </Button>
                  <Button variant="outline" onClick={openImp} data-testid="cont-import-undo-btn" title="Annulla un import titoli caricato per errore">
                    <Undo2 className="h-4 w-4 mr-2" /> Annulla import
                  </Button>
                </>
              )}
              {isDir && (
                <>
                  <Button variant="outline" onClick={() => setImpMgrOpen(true)} data-testid="cont-import-managers-btn" title="Autorizza altri colleghi a importare i titoli">
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Autorizza import
                  </Button>
                  <Button variant="outline" onClick={openMassMgr} data-testid="cont-massive-managers-btn" title="Autorizza colleghi alle operazioni massive (incasso/cancellazione) e consulta lo storico">
                    <ShieldCheck className="h-4 w-4 mr-2" /> Autorizza massivo
                  </Button>
                  <Button variant="outline" onClick={pagaCompagniaMassivo} data-testid="cont-paga-compagnia-massivo" title="Segna i titoli filtrati come pagati direttamente alla Compagnia (Assimedici, +Simple…): li toglie dall'arretrato senza movimenti di cassa">
                    <Landmark className="h-4 w-4 mr-2" /> Pagato a Compagnia (massivo)
                  </Button>
                </>
              )}
            </div>

            {canMassive && selIds.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 rounded-xl border border-teal-300 bg-teal-50 px-4 py-2.5 mb-2" data-testid="cont-bulk-bar">
                <span className="text-sm font-semibold text-teal-800" data-testid="cont-bulk-count">{selIds.length} titoli selezionati</span>
                <div className="flex-1" />
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => openBulk("incasso")} data-testid="cont-bulk-incassa">
                  <HandCoins className="h-4 w-4 mr-1.5" /> Incassa selezionati
                </Button>
                <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => openBulk("cancella")} data-testid="cont-bulk-elimina">
                  <Trash2 className="h-4 w-4 mr-1.5" /> Elimina selezionati
                </Button>
                <Button size="sm" variant="ghost" className="text-slate-500" onClick={clearSel} data-testid="cont-bulk-clear">Deseleziona</Button>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  {canMassive && <TableHead className="w-10"><Checkbox checked={allPageSelected} onCheckedChange={toggleSelAll} data-testid="cont-sel-all" aria-label="Seleziona tutti" /></TableHead>}
                  <SortableHead label="Stato" k="stato_effettivo" sort={sort} onSort={toggleSort} />
                  <SortableHead label="Cliente" k="cliente_nome" sort={sort} onSort={toggleSort} />
                  <SortableHead label="Polizza" k="numero_polizza" sort={sort} onSort={toggleSort} />
                  <SortableHead label="Compagnia" k="compagnia" sort={sort} onSort={toggleSort} />
                  <SortableHead label="Lordo" k="premio_lordo" sort={sort} onSort={toggleSort} />
                  <SortableHead label="Intermediario" k="intermediario" sort={sort} onSort={toggleSort} />
                  <SortableHead label="Scadenza" k="data_scadenza" sort={sort} onSort={toggleSort} />
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {items.length === 0 ? <TableRow><TableCell colSpan={canMassive ? 9 : 8} className="text-center text-slate-400 py-10">Nessun titolo.</TableCell></TableRow>
                    : items.map((t) => (
                      <TableRow key={t.titolo_id} data-testid={`cont-row-${t.titolo_id}`} className={selIds.includes(t.titolo_id) ? "bg-teal-50/50" : ""}>
                        {canMassive && <TableCell><Checkbox checked={selIds.includes(t.titolo_id)} onCheckedChange={() => toggleSel(t.titolo_id)} data-testid={`cont-sel-${t.titolo_id}`} /></TableCell>}
                        <TableCell><span className={`text-xs px-2 py-1 rounded-full font-medium ${BADGE[t.stato_effettivo]}`}>{STATO_LABEL[t.stato_effettivo]}</span></TableCell>
                        <TableCell className="font-medium text-slate-900">{t.cliente_nome}</TableCell>
                        <TableCell className="text-sm">{t.numero_polizza || "—"}</TableCell>
                        <TableCell className="text-sm">{t.compagnia || "—"}</TableCell>
                        <TableCell className="text-sm font-medium">{euro(t.premio_lordo)}</TableCell>
                        <TableCell className="text-sm">{t.intermediario || "—"}</TableCell>
                        <TableCell className={`text-sm ${isExpiredTitolo(t) ? "text-red-600 font-semibold" : ""}`}>
                          {fmtDate(t.data_scadenza)}
                          {isExpiredTitolo(t) && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 align-middle">scaduto</span>}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {["DA_INCASSARE", "ARRETRATO"].includes(t.stato_effettivo) &&
                            <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white mr-1" onClick={() => setInc({ ...t, modalita: "", data_incasso: new Date().toISOString().slice(0, 10), riferimento: "", collaboratore: t.intermediario || "", sede: t.sede || "", tipo_incasso: "proprio", produttore_fc: autoProduttore(t.intermediario) })} data-testid={`cont-incassa-${t.titolo_id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Incassa</Button>}
                          {["INCASSATO", "ANTICIPATO", "PAGATO_A_COMPAGNIA"].includes(t.stato_effettivo) &&
                            <Button size="sm" variant="ghost" className="h-8 text-slate-500" onClick={() => doStorna(t)} title="Storna" data-testid={`cont-storna-${t.titolo_id}`}><Undo2 className="h-3.5 w-3.5" /></Button>}
                          {t.stato_effettivo === "INCASSATO" && ["finanziamento_compass", "finanziamento_agos"].includes(t.modalita) &&
                            <Button size="sm" variant="ghost" className="h-8 text-orange-500" onClick={() => setSollecito({ titolo: t, canale: "email", destinatario: "", respinto: true })} title="Finanziamento respinto" data-testid={`cont-respinto-${t.titolo_id}`}><XCircle className="h-3.5 w-3.5" /></Button>}
                          <Button size="sm" variant="ghost" className="h-8" onClick={() => setDialog({ mode: "edit", data: { ...t } })}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="h-8 text-red-500" onClick={() => del(t)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-slate-400" data-testid="cont-titoli-count">{items.length} di {titoliTotal} titoli</p>
              {titoliHasMore && <Button variant="outline" size="sm" onClick={() => loadTitoli(titoliPage + 1, true)} data-testid="cont-titoli-load-more">Carica altri</Button>}
            </div>
          </TabsContent>

          <TabsContent value="casse">
            {cassaUnif && (
              <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-4 mb-4" data-testid="cont-cassa-unificata">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-teal-800"><Wallet className="h-5 w-5" /><span className="font-heading text-sm font-semibold">Cassa Agenzia — riepilogo dalla Prima Nota (fonte unica)</span></div>
                  <a href="/prima-nota" className="text-sm font-medium text-teal-700 underline hover:text-teal-900" data-testid="cont-cassa-link">Apri Prima Nota Cassa →</a>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                  <div className="rounded-lg bg-white border border-slate-200 p-3"><div className="text-[11px] uppercase text-slate-400">Saldo</div><div className={`text-lg font-bold ${cassaUnif.saldo < 0 ? "text-red-600" : "text-slate-900"}`}>{euro(cassaUnif.saldo)}</div></div>
                  <div className="rounded-lg bg-white border border-slate-200 p-3"><div className="text-[11px] uppercase text-slate-400">Entrate</div><div className="text-lg font-bold text-emerald-700">{euro(cassaUnif.totale_entrate)}</div></div>
                  <div className="rounded-lg bg-white border border-slate-200 p-3"><div className="text-[11px] uppercase text-slate-400">Da versare (cont.+ass.)</div><div className="text-lg font-bold text-amber-700">{euro(cassaUnif.da_versare)}</div></div>
                  <div className="rounded-lg bg-white border border-slate-200 p-3"><div className="text-[11px] uppercase text-slate-400">Sospesi aperti</div><div className="text-lg font-bold text-red-700">{euro(cassaUnif.sospesi_aperti)}</div></div>
                </div>
                <p className="text-[11px] text-slate-400 mt-2">La cassa fisica dell'Agenzia si gestisce in Prima Nota (contanti, assegni, versamenti, sospesi). Qui sotto restano i conti extra-cassa della Contabilità (finanziamenti, incasso diretto compagnia, storico).</p>
              </div>
            )}
            <div className="flex gap-2 mb-4 flex-wrap">
              <Button variant="outline" onClick={openVersa} data-testid="cont-versa-btn"><Landmark className="h-4 w-4 mr-2" /> Versa assegni in banca</Button>
              <Button variant="outline" onClick={() => openSospeso("SOSPESO_COMPASS")} data-testid="cont-chiudi-compass"><Layers className="h-4 w-4 mr-2" /> Chiudi sospeso Compass</Button>
              <Button variant="outline" onClick={() => openSospeso("SOSPESO_AGOS")} data-testid="cont-chiudi-agos"><Layers className="h-4 w-4 mr-2" /> Chiudi sospeso Agos</Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cont-conti-grid">
              {conti.map((c) => (
                <button key={c.codice} onClick={() => openConto(c)} className="text-left rounded-xl border border-slate-200 bg-white p-5 shadow-sm hover:border-teal-300 transition-colors" data-testid={`cont-conto-${c.codice}`}>
                  <div className="flex items-center gap-2 text-slate-500 text-sm"><Banknote className="h-4 w-4" /> {c.nome}</div>
                  <div className={`font-heading text-2xl mt-2 ${c.saldo < 0 ? "text-red-600" : "text-slate-900"}`}>{euro(c.saldo)}</div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mt-1">{c.tipo}</div>
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="crediti">
            <div className="relative mb-4 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={creditiQ} onChange={(e) => setCreditiQ(e.target.value)} placeholder="Cerca cliente, polizza o soggetto…" className="pl-9 bg-white" data-testid="cont-crediti-search" />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <SortableHead label="Tipo" k="tipo" sort={crSort} onSort={crToggle} />
                  <SortableHead label="Soggetto" k="soggetto" sort={crSort} onSort={crToggle} />
                  <SortableHead label="Cliente / Polizza" k="cliente_nome" sort={crSort} onSort={crToggle} />
                  <SortableHead label="Importo" k="importo" sort={crSort} onSort={crToggle} />
                  <SortableHead label="Data" k="data" sort={crSort} onSort={crToggle} />
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {crediti.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">Nessun credito aperto.</TableCell></TableRow>
                    : sortRows(crediti, crSort, ["importo"]).map((c) => (
                      <TableRow key={c.credito_id} data-testid={`cont-credito-${c.credito_id}`}>
                        <TableCell><span className="text-xs px-2 py-1 rounded-full bg-violet-100 text-violet-700 flex items-center gap-1 w-fit"><HandCoins className="h-3 w-3" /> {c.tipo === "cliente" ? "Anticipo cliente" : "Anticipo collaboratore"}</span></TableCell>
                        <TableCell className="font-medium text-slate-900">{c.soggetto || "—"}</TableCell>
                        <TableCell className="text-sm">{c.cliente_nome} · {c.numero_polizza || "—"}</TableCell>
                        <TableCell className="text-sm font-medium">{euro(c.importo)}</TableCell>
                        <TableCell className="text-sm">{fmtDate(c.data)}</TableCell>
                        <TableCell className="text-right"><Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setRecupera({ ...c, modalita: "bonifico", riferimento: "" })} data-testid={`cont-recupera-${c.credito_id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Recupera</Button></TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-slate-400" data-testid="cont-crediti-count">{crediti.length} di {creditiTotal} crediti</p>
              {creditiHasMore && <Button variant="outline" size="sm" onClick={() => loadCrediti(creditiPage + 1, true)} data-testid="cont-crediti-load-more">Carica altri</Button>}
            </div>
          </TabsContent>
          <TabsContent value="chiusura">
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 mb-6" data-testid="cont-chiusura-panel">
              <h3 className="font-heading text-lg text-slate-900 mb-1 flex items-center gap-2"><Calculator className="h-5 w-5 text-teal-600" /> Chiusura cassa</h3>
              <p className="text-sm text-slate-500 mb-4">Conta il reale di ogni cassa. In caso di scostamento è obbligatoria una Nota di Rettifica.</p>
              <div className="space-y-3">
                {(chiusura || []).map((r, i) => {
                  const diff = Math.round(((Number(r.contato) || 0) - r.atteso) * 100) / 100;
                  return (
                    <div key={r.codice} className="grid grid-cols-12 gap-3 items-center border-b border-slate-100 pb-3">
                      <div className="col-span-3"><div className="text-sm font-medium text-slate-800">{r.nome}</div><div className="text-[11px] text-slate-400">Atteso: {euro(r.atteso)}</div></div>
                      <div className="col-span-2"><Label className="text-[11px] text-slate-500">Contato</Label><Input type="number" value={r.contato} onChange={(e) => setChiusuraRow(i, "contato", e.target.value)} className="h-9" data-testid={`cont-chiusura-contato-${r.codice}`} /></div>
                      <div className="col-span-2 text-sm font-medium" style={{ color: diff === 0 ? "#059669" : "#dc2626" }}>{diff === 0 ? "Quadra" : `${diff > 0 ? "+" : ""}${euro(diff)}`}</div>
                      <div className="col-span-5">{diff !== 0 && <Input value={r.nota_rettifica} onChange={(e) => setChiusuraRow(i, "nota_rettifica", e.target.value)} placeholder="Nota di rettifica (obbligatoria)…" className="h-9" data-testid={`cont-chiusura-nota-${r.codice}`} />}</div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end mt-4"><Button onClick={doChiusura} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="cont-chiusura-confirm">Registra chiusura</Button></div>
            </div>
            {chiusure.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="cont-chiusure-storico">
                <h4 className="font-heading text-slate-900 mb-3">Storico chiusure</h4>
                <div className="space-y-2">
                  {chiusure.map((c) => {
                    const rett = (c.righe || []).filter((r) => r.differenza !== 0);
                    return (
                      <div key={c.chiusura_id} className="flex items-center justify-between border-b border-slate-100 py-2 text-sm">
                        <span className="text-slate-700">{new Date(c.created_at).toLocaleString("it-IT")}</span>
                        <span className="text-slate-500">{rett.length === 0 ? "Nessuna rettifica" : `${rett.length} rettifiche`}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="report">
            {report && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="cont-report-canale">
                    <h3 className="font-heading text-slate-900 mb-4">Volume per canale di incasso</h3>
                    {report.perCanale.length === 0 ? <p className="text-sm text-slate-400">Nessun incasso registrato.</p> : (
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie data={report.perCanale} dataKey="importo" nameKey="label" cx="50%" cy="50%" outerRadius={90} label={(e) => e.label}>
                            {report.perCanale.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                          </Pie>
                          <Tooltip formatter={(v) => euro(v)} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="cont-report-compagnia">
                    <h3 className="font-heading text-slate-900 mb-4">Volume premi per compagnia</h3>
                    {report.perCompagnia.length === 0 ? <p className="text-sm text-slate-400">Nessun dato.</p> : (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={report.perCompagnia}>
                          <XAxis dataKey="compagnia" fontSize={11} /><YAxis fontSize={11} />
                          <Tooltip formatter={(v) => euro(v)} /><Legend />
                          <Bar dataKey="incassato" name="Incassato" fill="#0d9488" stackId="a" />
                          <Bar dataKey="arretrato" name="Arretrato" fill="#ef4444" stackId="a" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto" data-testid="cont-report-arretrati">
                  <div className="flex items-center gap-2 p-4 border-b border-slate-100"><Bell className="h-4 w-4 text-red-500" /><h3 className="font-heading text-slate-900">Arretrati (ordinati per gravità)</h3></div>
                  <Table>
                    <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead>Polizza</TableHead><TableHead>Compagnia</TableHead><TableHead>Importo</TableHead><TableHead>Ritardo</TableHead><TableHead>Solleciti</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {report.arretrati.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">Nessun arretrato. 🎉</TableCell></TableRow>
                        : report.arretrati.map((t) => (
                          <TableRow key={t.titolo_id} data-testid={`cont-arretrato-${t.titolo_id}`}>
                            <TableCell className="font-medium text-slate-900">{t.cliente_nome}</TableCell>
                            <TableCell className="text-sm">{t.numero_polizza || "—"}</TableCell>
                            <TableCell className="text-sm">{t.compagnia || "—"}</TableCell>
                            <TableCell className="text-sm font-medium">{euro(t.premio_lordo)}</TableCell>
                            <TableCell><span className={`text-xs px-2 py-1 rounded-full font-medium ${t.giorni_ritardo > 30 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{t.giorni_ritardo} gg</span></TableCell>
                            <TableCell className="text-sm text-slate-500">{t.solleciti || 0}</TableCell>
                            <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => setSollecito({ titolo: t, canale: "email", destinatario: "", respinto: false })} data-testid={`cont-sollecita-${t.titolo_id}`}><Send className="h-3.5 w-3.5 mr-1" /> Sollecita</Button></TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="bonifici"><BonificiTab /></TabsContent>
          <TabsContent value="estratti"><EstrattiTab /></TabsContent>
          <TabsContent value="subagenzia"><SubagenziaTab /></TabsContent>
          <TabsContent value="quietanzamento"><QuietanzamentoTab compagnie={compagnie} /></TabsContent>
          <TabsContent value="assimoco"><AssimocoTab /></TabsContent>
          <TabsContent value="riconciliazione"><RiconciliazioneTab /></TabsContent>
        </Tabs>
      </main>

      {/* Nuovo / Modifica titolo */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{dialog?.mode === "edit" ? "Modifica titolo" : "Nuovo titolo"}</DialogTitle>
            <DialogDescription>Rata di premio da incassare, collegata al cliente e all'intermediario.</DialogDescription></DialogHeader>
          {dialog && (
            <div className="grid grid-cols-2 gap-3 py-2">
              <div className="col-span-2 space-y-1.5"><Label className="text-sm text-slate-600">Cliente</Label>
                <ClientePicker nome={dialog.data.cliente_nome} onPick={(id, nome) => setDialog((s) => ({ ...s, data: { ...s.data, cliente_id: id, cliente_nome: nome } }))} testid="cont-cliente" /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">N° polizza</Label><Input value={dialog.data.numero_polizza} onChange={(e) => setD("numero_polizza", e.target.value)} data-testid="cont-polizza" /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Compagnia</Label>
                <Select value={dialog.data.compagnia} onValueChange={(v) => setD("compagnia", v)}><SelectTrigger data-testid="cont-compagnia"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>{compagnie.map((c) => <SelectItem key={c.compagnia_id} value={c.nome}>{c.nome}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Ramo</Label><Input value={dialog.data.ramo} onChange={(e) => setD("ramo", e.target.value)} placeholder="RC Auto, Vita…" /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Intermediario</Label>
                <Select value={dialog.data.intermediario} onValueChange={(v) => setD("intermediario", v)}><SelectTrigger data-testid="cont-intermediario"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>{intermediari.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Premio lordo (€)</Label><Input type="number" value={dialog.data.premio_lordo} onChange={(e) => setD("premio_lordo", e.target.value)} data-testid="cont-lordo" /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Premio netto (€) <span className="text-slate-400">da volturare</span></Label><Input type="number" value={dialog.data.premio_netto} onChange={(e) => setD("premio_netto", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Frazionamento</Label>
                <Select value={dialog.data.frazionamento} onValueChange={(v) => setD("frazionamento", v)}><SelectTrigger data-testid="cont-frazionamento"><SelectValue /></SelectTrigger>
                  <SelectContent>{FRAZ.map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Data effetto</Label><DateInputIT value={dialog.data.data_effetto} onChange={(v) => setD("data_effetto", v)} /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Data scadenza</Label><DateInputIT value={dialog.data.data_scadenza} onChange={(v) => setD("data_scadenza", v)} testid="cont-scadenza" /></div>
              <div className="col-span-2 space-y-1.5"><Label className="text-sm text-slate-600">Note</Label><Textarea value={dialog.data.note} onChange={(e) => setD("note", e.target.value)} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={save} disabled={!dialog?.data.cliente_nome || !(Number(dialog?.data.premio_lordo) > 0)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="cont-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incassa */}
      <Dialog open={!!inc} onOpenChange={(o) => !o && setInc(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Incassa titolo — {inc?.cliente_nome}</DialogTitle>
            <DialogDescription>{inc && euro(inc.premio_lordo)} · pol. {inc?.numero_polizza || "—"}. Scegli la modalità: il denaro verrà instradato nella cassa corretta.</DialogDescription></DialogHeader>
          {inc && (
            <div className="grid grid-cols-2 gap-3 py-2">
              <div className="col-span-2 space-y-1.5"><Label className="text-sm text-slate-600">Modalità di pagamento</Label>
                <Select value={inc.modalita} onValueChange={(v) => setInc({ ...inc, modalita: v })}><SelectTrigger data-testid="cont-inc-modalita"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>{modalita.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Data incasso</Label><DateInputIT value={inc.data_incasso} onChange={(v) => setInc({ ...inc, data_incasso: v })} /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Sede</Label>
                <Select value={inc.sede || "none"} onValueChange={(v) => setInc({ ...inc, sede: v === "none" ? "" : v })}><SelectTrigger data-testid="cont-inc-sede"><SelectValue placeholder="Sede…" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Non specificata</SelectItem><SelectItem value="Macomer">Macomer</SelectItem><SelectItem value="Bonorva">Bonorva</SelectItem><SelectItem value="Altro">Altro</SelectItem></SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Tipo incasso</Label>
                <Select value={inc.tipo_incasso} onValueChange={(v) => setInc({ ...inc, tipo_incasso: v })}><SelectTrigger data-testid="cont-inc-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="proprio">Proprio (collaboratore)</SelectItem><SelectItem value="agenzia">Di Agenzia</SelectItem></SelectContent></Select></div>
              {curMod?.riferimento && <div className="space-y-1.5"><Label className="text-sm text-slate-600">Riferimento (TRN/CRO)</Label><Input value={inc.riferimento} onChange={(e) => setInc({ ...inc, riferimento: e.target.value })} data-testid="cont-inc-riferimento" placeholder="TRN / CRO / data valuta" /></div>}
              {curMod?.credito === "collaboratore" && <div className="space-y-1.5"><Label className="text-sm text-slate-600">Collaboratore</Label>
                <Select value={inc.collaboratore} onValueChange={(v) => setInc({ ...inc, collaboratore: v })}><SelectTrigger data-testid="cont-inc-collab"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>{intermediari.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select></div>}
              {cashModalita.includes(inc.modalita) && (
                <div className="col-span-2 space-y-1.5"><Label className="text-sm text-slate-600">Foglio Cassa (produttore)</Label>
                  <Select value={inc.produttore_fc || "Agenzia"} onValueChange={(v) => setInc({ ...inc, produttore_fc: v })}><SelectTrigger data-testid="cont-inc-produttore-fc"><SelectValue /></SelectTrigger>
                    <SelectContent>{produttoriFc.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
                  <p className="text-[11px] text-slate-400">L'incasso viene registrato su questo Foglio Cassa e confluisce in Prima Nota. Preselezionato automaticamente dall'intermediario, modificabile.</p></div>
              )}
              {incDup && cashModalita.includes(inc.modalita) && (
                <div className="col-span-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-start gap-2" data-testid="cont-inc-dup-warning">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                  <span>Attenzione: questo titolo risulta <b>già in un Foglio Cassa</b> ({incDup.produttore}, {fmtIT(incDup.data)}, € {incDup.importo}). Registrandolo di nuovo rischi un doppio incasso.</span>
                </div>
              )}
              {curMod && (
                <div className="col-span-2 text-xs text-slate-500 bg-teal-50 border border-teal-100 rounded-lg p-2" data-testid="cont-inc-info">
                  {cashModalita.includes(inc.modalita) ? <>Confluisce nel <b>Foglio Cassa {inc.produttore_fc || "Agenzia"}</b> e in <b>Prima Nota</b> (catena unica, nessun doppio conteggio).{["bonifico", "pos"].includes(inc.modalita) && " Bonifico/POS: escluso dalla rimessa (già in banca)."}</>
                    : curMod.credito ? <>Nessun incasso in cassa: si crea un <b>credito {curMod.credito === "cliente" ? "verso il cliente" : "del collaboratore"}</b> (titolo <b>Anticipato</b>).</>
                    : curMod.conto ? <>Movimento extra-cassa sul conto <b>{curMod.conto.replace("_", " ")}</b> (Contabilità).</>
                    : <>Titolo segnato come <b>incassato dalla compagnia</b> (nessun movimento di cassa).</>}
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button onClick={() => doIncassa()} disabled={!inc?.modalita} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cont-inc-confirm">Conferma incasso</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movimenti conto */}
      <Dialog open={!!contoView} onOpenChange={(o) => !o && setContoView(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{contoView?.conto?.nome} — saldo {euro(contoView?.conto?.saldo)}</DialogTitle>
            <DialogDescription>Movimenti del conto</DialogDescription></DialogHeader>
          <div className="space-y-2" data-testid="cont-conto-movimenti">
            {(contoView?.movimenti || []).length === 0 ? <p className="text-sm text-slate-400">Nessun movimento.</p>
              : contoView.movimenti.map((m) => (
                <div key={m.mov_id} className="flex items-center justify-between border-b border-slate-100 py-2">
                  <div><p className="text-sm text-slate-700">{m.causale}</p><p className="text-[11px] text-slate-400">{fmtIT(m.data)}</p></div>
                  <span className={`text-sm font-medium ${m.tipo === "entrata" ? "text-emerald-600" : "text-red-600"}`}>{m.tipo === "entrata" ? "+" : "−"} {euro(m.importo)}</span>
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Versa assegni */}
      <Dialog open={!!versa} onOpenChange={(o) => !o && setVersa(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Versa assegni in banca</DialogTitle>
            <DialogDescription>Seleziona gli assegni da portare in banca: il totale passa da "Assegni da versare" al Conto Corrente.</DialogDescription></DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto" data-testid="cont-versa-list">
            {(versa?.movs || []).length === 0 ? <p className="text-sm text-slate-400">Nessun assegno da versare.</p>
              : versa.movs.map((m) => (
                <label key={m.mov_id} className="flex items-center gap-2 text-sm cursor-pointer border-b border-slate-100 py-2">
                  <Checkbox checked={versa.selected.includes(m.mov_id)} onCheckedChange={() => toggleVersa(m.mov_id)} data-testid={`cont-versa-${m.mov_id}`} />
                  <span className="flex-1 truncate">{m.causale}</span>
                  <span className="font-medium">{euro(m.importo)}</span>
                </label>
              ))}
          </div>
          <DialogFooter><Button onClick={doVersa} disabled={!versa?.selected.length} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="cont-versa-confirm">Versa {versa ? euro(versa.movs.filter((m) => versa.selected.includes(m.mov_id)).reduce((a, m) => a + m.importo, 0)) : ""}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chiudi sospeso */}
      <Dialog open={!!sospeso} onOpenChange={(o) => !o && setSospeso(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Chiudi sospeso {sospeso?.codice?.replace("SOSPESO_", "")}</DialogTitle>
            <DialogDescription>Seleziona i titoli del lotto liquidato dalla finanziaria e registra il bonifico cumulativo ricevuto.</DialogDescription></DialogHeader>
          {sospeso && (
            <div className="space-y-3">
              <div className="space-y-2 max-h-[35vh] overflow-y-auto" data-testid="cont-sospeso-list">
                {sospeso.movs.length === 0 ? <p className="text-sm text-slate-400">Nessun sospeso aperto.</p>
                  : sospeso.movs.map((m) => (
                    <label key={m.mov_id} className="flex items-center gap-2 text-sm cursor-pointer border-b border-slate-100 py-2">
                      <Checkbox checked={sospeso.selected.includes(m.mov_id)} onCheckedChange={() => toggleSospeso(m.mov_id)} data-testid={`cont-sospeso-${m.mov_id}`} />
                      <span className="flex-1 truncate">{m.causale}</span>
                      <span className="font-medium">{euro(m.importo)}</span>
                    </label>
                  ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-sm text-slate-600">Riferimento bonifico</Label><Input value={sospeso.riferimento} onChange={(e) => setSospeso((s) => ({ ...s, riferimento: e.target.value }))} data-testid="cont-sospeso-rif" /></div>
                <div className="space-y-1.5"><Label className="text-sm text-slate-600">Importo bonifico <span className="text-slate-400">(opz.)</span></Label><Input type="number" value={sospeso.importo_bonifico} onChange={(e) => setSospeso((s) => ({ ...s, importo_bonifico: e.target.value }))} placeholder={euro(sospeso.movs.filter((m) => sospeso.selected.includes(m.mov_id)).reduce((a, m) => a + m.importo, 0))} data-testid="cont-sospeso-imp" /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={doSospeso} disabled={!sospeso?.selected.length} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="cont-sospeso-confirm">Chiudi sospeso</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recupera credito */}
      <Dialog open={!!recupera} onOpenChange={(o) => !o && setRecupera(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Recupera anticipo — {recupera?.cliente_nome}</DialogTitle>
            <DialogDescription>{recupera && euro(recupera.importo)}. L'incasso entra nella cassa scelta e il titolo torna Incassato.</DialogDescription></DialogHeader>
          {recupera && (
            <div className="grid grid-cols-2 gap-3 py-2">
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Modalità</Label>
                <Select value={recupera.modalita} onValueChange={(v) => setRecupera((r) => ({ ...r, modalita: v, riferimento: v === "contante" ? "" : r.riferimento }))}><SelectTrigger data-testid="cont-recupera-modalita"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="bonifico">Bonifico → Banca</SelectItem><SelectItem value="contante">Contante → Cassa</SelectItem></SelectContent></Select></div>
              {recupera.modalita === "bonifico" && <div className="space-y-1.5"><Label className="text-sm text-slate-600">Riferimento</Label><Input value={recupera.riferimento} onChange={(e) => setRecupera((r) => ({ ...r, riferimento: e.target.value }))} data-testid="cont-recupera-rif" /></div>}
            </div>
          )}
          <DialogFooter><Button onClick={doRecupera} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cont-recupera-confirm">Conferma recupero</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sollecito / Finanziamento respinto */}
      <Dialog open={!!sollecito} onOpenChange={(o) => !o && setSollecito(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">{sollecito?.respinto ? "Finanziamento respinto" : "Sollecito di pagamento"} — {sollecito?.titolo?.cliente_nome}</DialogTitle>
            <DialogDescription>
              {sollecito?.respinto
                ? "Il titolo tornerà 'Da incassare' (storno del sospeso) e verrà inviato al cliente un avviso per regolarizzare con altro metodo."
                : "Invio di un promemoria di pagamento al cliente."} L'invio è al momento <b>simulato</b> (nessun provider collegato).
            </DialogDescription>
          </DialogHeader>
          {sollecito && (
            <div className="grid grid-cols-2 gap-3 py-2">
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Canale</Label>
                <Select value={sollecito.canale} onValueChange={(v) => setSollecito((s) => ({ ...s, canale: v }))}><SelectTrigger data-testid="cont-sollecito-canale"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="email">Email</SelectItem><SelectItem value="sms">SMS</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem></SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Destinatario</Label><Input value={sollecito.destinatario} onChange={(e) => setSollecito((s) => ({ ...s, destinatario: e.target.value }))} placeholder={sollecito.canale === "email" ? "email@cliente.it" : "numero"} data-testid="cont-sollecito-dest" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={doSollecito} className={`text-white ${sollecito?.respinto ? "bg-orange-600 hover:bg-orange-700" : "bg-teal-600 hover:bg-teal-700"}`} data-testid="cont-sollecito-confirm"><Send className="h-4 w-4 mr-2" /> {sollecito?.respinto ? "Riapri e invia (simulato)" : "Invia (simulato)"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma operazione massiva (incasso/cancellazione) — doppia conferma */}
      <Dialog open={!!bulk} onOpenChange={(o) => !o && setBulk(null)}>
        <DialogContent className="max-w-md" data-testid="cont-bulk-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {bulk?.mode === "incasso"
                ? <><HandCoins className="h-5 w-5 text-emerald-600" /> Incasso massivo</>
                : <><AlertTriangle className="h-5 w-5 text-red-600" /> Cancellazione massiva</>}
            </DialogTitle>
            <DialogDescription>
              {bulk?.mode === "incasso"
                ? `Stai per incassare ${bulk?.candidati} titoli per un totale di ${euro(bulk?.importo)}. Vengono elaborati solo i titoli Da incassare / Arretrato tra i selezionati.`
                : `Stai per eliminare definitivamente ${bulk?.candidati} titoli (${euro(bulk?.importo)}). I titoli già incassati sono protetti e non verranno toccati${bulk?.protetti ? ` (${bulk.protetti} protetti tra i selezionati)` : ""}.`}
            </DialogDescription>
          </DialogHeader>
          {bulk && (
            <div className="space-y-3 py-1">
              {bulk.mode === "incasso" && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-sm text-slate-600">Modalità di pagamento</Label>
                    <Select value={bulk.modalita} onValueChange={(v) => setBulk((b) => ({ ...b, modalita: v }))}>
                      <SelectTrigger data-testid="cont-bulk-modalita"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {modalita.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm text-slate-600">Data incasso</Label>
                    <DateInputIT value={bulk.data_incasso} onChange={(v) => setBulk((b) => ({ ...b, data_incasso: v }))} testid="cont-bulk-data" />
                  </div>
                </>
              )}
              <div className={`rounded-lg border p-3 ${bulk.mode === "incasso" ? "border-emerald-200 bg-emerald-50/60" : "border-red-200 bg-red-50/60"}`}>
                <Label className="text-sm text-slate-700">
                  Per confermare digita <b>{bulk.mode === "incasso" ? "INCASSA" : "ELIMINA"}</b>
                </Label>
                <Input value={bulk.conferma} onChange={(e) => setBulk((b) => ({ ...b, conferma: e.target.value }))}
                  placeholder={bulk.mode === "incasso" ? "INCASSA" : "ELIMINA"} className="mt-2 bg-white" data-testid="cont-bulk-conferma" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulk(null)} data-testid="cont-bulk-cancel">Annulla</Button>
            <Button onClick={doBulk}
              variant={bulk?.mode === "incasso" ? "default" : "destructive"}
              disabled={(bulk?.conferma || "").trim().toUpperCase() !== (bulk?.mode === "incasso" ? "INCASSA" : "ELIMINA")}
              data-testid="cont-bulk-confirm">
              {bulk?.mode === "incasso" ? `Incassa ${bulk?.candidati} titoli` : `Elimina ${bulk?.candidati} titoli`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Autorizzati operazioni massive + Storico (admin) */}
      <Dialog open={massMgrOpen} onOpenChange={setMassMgrOpen}>
        <DialogContent className="max-w-2xl" data-testid="cont-massive-managers-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-teal-600" /> Operazioni massive — autorizzati e storico</DialogTitle>
            <DialogDescription>La Direzione è sempre autorizzata. Aggiungi le email dei colleghi che possono eseguire incasso e cancellazione massiva dei titoli (con flag e doppia conferma).</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            {massMgrs.length === 0 && <p className="text-xs text-slate-400">Nessun collega aggiuntivo autorizzato (solo la Direzione).</p>}
            {massMgrs.map((e) => (
              <div key={e} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5" data-testid={`cont-massmgr-${e}`}>
                <span className="text-sm text-slate-700">{e}</span>
                <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => removeMassMgr(e)} data-testid={`cont-massmgr-remove-${e}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
            <div className="flex gap-2 pt-1">
              <Input value={massMgrNew} onChange={(ev) => setMassMgrNew(ev.target.value)} onKeyDown={(ev) => ev.key === "Enter" && addMassMgr()} placeholder="email@assietica.it" className="bg-white" data-testid="cont-massmgr-input" />
              <Button onClick={addMassMgr} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="cont-massmgr-add"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
            </div>
          </div>
          <div className="mt-1">
            <div className="flex items-center gap-2 text-slate-700 mb-1"><History className="h-4 w-4" /><h4 className="font-heading text-sm font-semibold">Storico operazioni massive</h4></div>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200" data-testid="cont-massive-audit">
              {auditRows.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">Nessuna operazione massiva registrata.</p> : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Data</TableHead><TableHead>Utente</TableHead><TableHead>Azione</TableHead>
                    <TableHead className="text-right">Titoli</TableHead><TableHead className="text-right">Importo</TableHead>
                    <TableHead className="text-right">Annulla</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {auditRows.map((a) => (
                      <TableRow key={a.audit_id} data-testid={`cont-audit-${a.audit_id}`}>
                        <TableCell className="text-xs text-slate-500">{(a.timestamp || "").slice(0, 16).replace("T", " ")}</TableCell>
                        <TableCell className="text-sm">{a.utente}</TableCell>
                        <TableCell className="text-sm">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${a.azione === "incasso" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                            {a.azione === "incasso" ? `Incasso · ${a.dettaglio?.modalita_label || ""}` : "Cancellazione"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">{a.count}</TableCell>
                        <TableCell className="text-right text-sm">{euro(a.importo_totale)}</TableCell>
                        <TableCell className="text-right">
                          {a.annullata
                            ? <span className="text-[11px] text-slate-400" data-testid={`cont-audit-annullata-${a.audit_id}`}>Annullata</span>
                            : (a.azione === "incasso"
                                ? <Button size="sm" variant="ghost" className="h-7 text-amber-700 hover:bg-amber-50" onClick={() => annullaMassiva(a.audit_id)} data-testid={`cont-audit-annulla-${a.audit_id}`}><Undo2 className="h-3.5 w-3.5 mr-1" /> Annulla</Button>
                                : <span className="text-[11px] text-slate-300">—</span>)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setMassMgrOpen(false)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Autorizza import (admin) */}
      <Dialog open={impMgrOpen} onOpenChange={setImpMgrOpen}>
        <DialogContent className="max-w-lg" data-testid="cont-import-managers-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Autorizza import titoli</DialogTitle>
            <DialogDescription>La Direzione è sempre autorizzata. Aggiungi qui le email dei colleghi dell'amministrazione (es. Melania) che possono importare i titoli da Assieasy.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {impMgrs.length === 0 && <p className="text-xs text-slate-400">Nessun collega aggiuntivo autorizzato (solo la Direzione).</p>}
            {impMgrs.map((e) => (
              <div key={e} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5" data-testid={`cont-impmgr-${e}`}>
                <span className="text-sm text-slate-700">{e}</span>
                <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => removeImpMgr(e)} data-testid={`cont-impmgr-remove-${e}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
            <div className="flex gap-2 pt-1">
              <Input value={impMgrNew} onChange={(ev) => setImpMgrNew(ev.target.value)} onKeyDown={(ev) => ev.key === "Enter" && addImpMgr()} placeholder="email@assietica.it" className="bg-white" data-testid="cont-impmgr-input" />
              <Button onClick={addImpMgr} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="cont-impmgr-add"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setImpMgrOpen(false)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Annulla import titoli */}
      <Dialog open={impOpen} onOpenChange={setImpOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Annulla import titoli</DialogTitle>
            <DialogDescription>Elenco degli import da Assieasy. "Annulla" elimina i titoli caricati con quel file non ancora incassati (quelli già incassati vengono conservati).</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto" data-testid="cont-import-batches">
            {impBatches.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">Nessun import registrato.</p>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>File</TableHead><TableHead className="text-right">Titoli</TableHead>
                  <TableHead className="text-right">Tot. lordo</TableHead><TableHead>Data</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {impBatches.map((b) => (
                    <TableRow key={b.id} data-testid={`cont-import-batch-${b.id}`}>
                      <TableCell className="text-sm max-w-[220px] truncate" title={b.filename}>{b.filename}</TableCell>
                      <TableCell className="text-right text-sm">{b.importati}</TableCell>
                      <TableCell className="text-right text-sm">{euro(b.tot_lordo || 0)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{(b.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => delImpBatch(b)} data-testid={`cont-import-del-${b.id}`}>
                          <Trash2 className="h-4 w-4 mr-1" /> Annulla
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <div className="mt-2 rounded-xl border border-red-200 bg-red-50/60 p-4" data-testid="cont-cleanup-panel">
            <div className="flex items-center gap-2 text-red-700 mb-1"><XCircle className="h-4 w-4" /><h4 className="font-heading text-sm font-semibold">Pulizia massiva portafoglio (definitiva)</h4></div>
            <p className="text-xs text-slate-600 mb-3">Elimina in blocco i titoli NON incassati. I titoli già incassati non vengono mai toccati{cleanup ? ` (${cleanup.incassati_protetti} protetti)` : ""}. Operazione irreversibile: digita <b>ELIMINA</b> per abilitare.</p>
            <Input value={confirmTxt} onChange={(e) => setConfirmTxt(e.target.value)} placeholder="Digita ELIMINA per confermare" className="mb-3 max-w-xs" data-testid="cont-cleanup-confirm" />
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-between border-red-200 text-slate-700 hover:bg-red-50" disabled={!cleanup || cleanup.lordo_zero === 0} onClick={() => doCleanup("lordo_zero", cleanup?.lordo_zero)} data-testid="cont-cleanup-lordo-zero">
                <span>Solo titoli importati con Lordo € 0 (errati)</span><span className="font-semibold">{cleanup?.lordo_zero ?? "—"}</span>
              </Button>
              <Button variant="outline" className="w-full justify-between border-red-200 text-slate-700 hover:bg-red-50" disabled={!cleanup || cleanup.importati === 0} onClick={() => doCleanup("importati", cleanup?.importati)} data-testid="cont-cleanup-importati">
                <span>Tutti i titoli importati da Assieasy non incassati</span><span className="font-semibold">{cleanup?.importati ?? "—"}</span>
              </Button>
              <Button variant="destructive" className="w-full justify-between" disabled={!cleanup || cleanup.tutti_non_incassati === 0} onClick={() => doCleanup("tutti_non_incassati", cleanup?.tutti_non_incassati)} data-testid="cont-cleanup-tutti">
                <span>Svuota TUTTO il portafoglio non incassato</span><span className="font-semibold">{cleanup?.tutti_non_incassati ?? "—"}</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Riepilogo post-import titoli */}
      <Dialog open={!!impResult} onOpenChange={(o) => !o && setImpResult(null)}>
        <DialogContent className="max-w-md" data-testid="cont-import-result">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Import completato</DialogTitle>
            <DialogDescription>Controlla il riepilogo. Se hai caricato il file sbagliato puoi annullarlo subito.</DialogDescription>
          </DialogHeader>
          {impResult && (
            <div className="py-1 space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2"><div className="text-[11px] uppercase tracking-wide text-slate-400">Importati</div><div className="font-heading text-xl text-emerald-600">{impResult.importati}</div></div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2"><div className="text-[11px] uppercase tracking-wide text-slate-400">Scartati</div><div className="font-heading text-xl text-slate-500">{impResult.saltati}</div></div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2"><div className="text-[11px] uppercase tracking-wide text-slate-400">Tot. lordo</div><div className="font-heading text-lg text-slate-900">{euro(impResult.tot_lordo || 0)}</div></div>
              </div>
              {(impResult.per_compagnia || []).length > 0 && (
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-50 max-h-48 overflow-y-auto">
                  {impResult.per_compagnia.map((c) => (
                    <div key={c.compagnia} className="flex justify-between px-3 py-1.5 text-sm"><span className="text-slate-700">{c.compagnia}</span><span className="font-medium text-slate-900">{c.titoli}</span></div>
                  ))}
                </div>
              )}
              {impResult.tot_lordo === 0 && impResult.importati > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">Attenzione: totale lordo € 0. Verifica di aver caricato il file <b>Quietanzamenti</b> giusto (con colonna "Premio Lordo").</div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" onClick={undoImpResult} data-testid="cont-import-result-undo"><Undo2 className="h-4 w-4 mr-2" /> Annulla questo import</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => setImpResult(null)} data-testid="cont-import-result-ok">Va bene, chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, cls = "text-slate-900", sub }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`font-heading text-2xl mt-1 ${cls}`}>{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

const SortableHead = ({ label, k, sort, onSort, className = "" }) => {
  const active = sort.key === k;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <TableHead className={className}>
      <button type="button" onClick={() => onSort(k)} data-testid={`cont-sort-${k}`}
        className={`inline-flex items-center gap-1 -ml-1 px-1 py-0.5 rounded hover:bg-slate-100 transition-colors ${active ? "text-teal-600 font-semibold" : "text-slate-600"}`}>
        {label}
        <Icon className={`h-3.5 w-3.5 ${active ? "opacity-100" : "opacity-40"}`} />
      </button>
    </TableHead>
  );
};
